import _regeneratorRuntime from '@babel/runtime/regenerator';
import _asyncToGenerator from '@babel/runtime/helpers/asyncToGenerator';
import _extends from '@babel/runtime/helpers/extends';

/**
 * @fileoverview
 * The functions exported by this module make it easier (and safer) to override
 * foreign object methods (in a modular way) and respond to or modify their
 * invocation. The primary feature is the ability to override a method without
 * worrying if it's already been overridden somewhere else in the codebase. It
 * also allows for safe restoring of an overridden method by only fully
 * restoring a method once all overrides have been removed.
 */
var instances = [];
/**
 * A class that wraps a foreign object method and emit events before and
 * after the original method is called.
 */

var MethodChain =
/*#__PURE__*/
function () {
  MethodChain.add = function add(context, methodName, methodOverride) {
    getOrCreateMethodChain(context, methodName).add(methodOverride);
  };

  MethodChain.remove = function remove(context, methodName, methodOverride) {
    var methodChain = getMethodChain(context, methodName);

    if (methodChain) {
      methodChain.remove(methodOverride);
    }
  };

  function MethodChain(context, methodName) {
    var _this = this;

    this.context = context;
    this.methodName = methodName;
    this.originalMethodReference = context[methodName];
    this.methodChain = [];
    this.boundMethodChain = []; // Wraps the original method.

    this.wrappedMethod = function () {
      var lastBoundMethod = _this.boundMethodChain[_this.boundMethodChain.length - 1];
      return lastBoundMethod.apply(void 0, arguments);
    }; // Override original method with the wrapped one.


    context[methodName] = this.wrappedMethod;
  }

  var _proto = MethodChain.prototype;

  _proto.add = function add(overrideMethod) {
    this.methodChain.push(overrideMethod);
    this.rebindMethodChain();
  };

  _proto.remove = function remove(overrideMethod) {
    var index = this.methodChain.indexOf(overrideMethod);

    if (index > -1) {
      this.methodChain.splice(index, 1);

      if (this.methodChain.length > 0) {
        this.rebindMethodChain();
      } else {
        this.destory();
      }
    }
  };

  _proto.rebindMethodChain = function rebindMethodChain() {
    this.boundMethodChain = [];

    for (var method, i = 0; method = this.methodChain[i]; i++) {
      var previousMethod = this.boundMethodChain[i - 1] || this.originalMethodReference.bind(this.context);
      this.boundMethodChain.push(method(previousMethod));
    }
  };

  _proto.destory = function destory() {
    var index = instances.indexOf(this);

    if (index > -1) {
      instances.splice(index, -1);
      this.context[this.methodName] = this.originalMethodReference;
    }
  };

  return MethodChain;
}();

function getMethodChain(context, methodName) {
  return instances.filter(function (h) {
    return h.context === context && h.methodName === methodName;
  })[0];
}

function getOrCreateMethodChain(context, methodName) {
  var methodChain = getMethodChain(context, methodName);

  if (!methodChain) {
    methodChain = new MethodChain(context, methodName);
    instances.push(methodChain);
  }

  return methodChain;
}

/**
 * When hash router is used, the pathname that is used to match router
 * is the part after '#' - 'http://0.0.0.0/#/page2', '/page2' is the router.
 * @param {Object} location object contains pathname and hash
 */

function getPathname(location) {
  if (location.hash && location.hash.startsWith('#/')) {
    // hash router
    var hi = location.hash.lastIndexOf('#');
    return hi === 0 ? location.hash.substring(1) : location.hash.substring(1, hi); // remove tail anchor
  }

  return location.pathname;
}

function getPathSearch() {
  return location.pathname + location.hash + location.search;
}

function getAbsolutePath() {
  return location.host + location.pathname + location.hash;
}

function parseQueryStringToObj(href) {
  href = href || '';
  var result = {};
  var q = href.indexOf('?');

  if (q !== -1) {
    var query = href.substring(q + 1);
    query = query.split('#')[0]; // remove tail hash (anchor)

    query.split('&').forEach(function (part) {
      if (part) {
        var eq = part.indexOf('=');
        var key = eq > -1 ? part.substring(0, eq) : part;
        var value = eq > -1 ? decodeURIComponent(part.substring(eq + 1)) : '';
        result[key] = value;
      }
    });
  }

  return result;
}

function isEmptyObj(obj) {
  return Object.keys(obj).length <= 0;
}
/**
 * A class that hooks history API and send out auto page view event when necessary.
 */


var AutoTracker =
/*#__PURE__*/
function () {
  function AutoTracker(opts) {
    // Feature detects to prevent errors in unsupporting browsers.
    if (!self.window || !self.window.addEventListener) {
      console.warn('[AutoTracker]', 'not work unsupporting browser');
      return;
    }

    var defaultOpts = {
      shouldTrackHistoryChange: this.shouldTrackHistoryChange,
      trackReplaceState: false,
      matchRoutes: null,
      bridgeCallHandler: null
    };
    this.opts = _extends({}, defaultOpts, opts);
    this.path = getPathSearch(); // detect if history entry changed

    this.pathname = getPathname(location);
    this.pushStateOverride = this.pushStateOverride.bind(this);
    this.replaceStateOverride = this.replaceStateOverride.bind(this);
    this.handlePopState = this.handlePopState.bind(this);
    this.handleHashChange = this.handleHashChange.bind(this);
    this.handleLoad = this.handleLoad.bind(this); // Watch for history changes.

    if (self.history && self.history.pushState) {
      MethodChain.add(history, 'pushState', this.pushStateOverride);
      MethodChain.add(history, 'replaceState', this.replaceStateOverride);
      window.addEventListener('popstate', this.handlePopState);
    } else {
      // Support old browsers like IE 9 or below.
      window.addEventListener('hashchange', this.handleHashChange);
    }

    this.handleLoad();
  }

  var _proto = AutoTracker.prototype;

  _proto.pushStateOverride = function pushStateOverride(originalMethod) {
    var _this = this;

    return function () {
      originalMethod.apply(void 0, arguments);

      _this.handleHistoryChange(true);
    };
  };

  _proto.replaceStateOverride = function replaceStateOverride(originalMethod) {
    var _this2 = this;

    return function () {
      originalMethod.apply(void 0, arguments);

      _this2.handleHistoryChange(false);
    };
  };

  _proto.handlePopState = function handlePopState() {
    this.handleHistoryChange(true);
  };

  _proto.handleHashChange = function handleHashChange() {
    this.handleHistoryChange(true);
  };

  _proto.handleLoad = function handleLoad() {
    var _this3 = this;

    // Snapshot!
    var data = {
      isOnload: true,
      referrer: self.document.referrer,
      href: self.location.href,
      hostname: self.location.hostname,
      pathname: this.pathname,
      absolutePath: getAbsolutePath()
    };
    setTimeout(function () {
      try {
        _this3.sendPageview(data);
      } catch (err) {
        console.warn('[AutoTracker]', err);
      }
    }, 0);
  }
  /**
   * Generate auto_view event and send it to the native bridge interface.
   * @param {Object} args to generate auto_view event
   */
  ;

  _proto.sendPageview =
  /*#__PURE__*/
  function () {
    var _sendPageview = _asyncToGenerator(
    /*#__PURE__*/
    _regeneratorRuntime.mark(function _callee(args) {
      var timestamp, params, auto_pv_common, event;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              timestamp = Date.now();
              params = parseQueryStringToObj(args.href);
              auto_pv_common = {
                auto_refer: args.referrer,
                auto_pathname: args.pathname,
                auto_url: args.href,
                start_time: timestamp
              };

              if (!isEmptyObj(params)) {
                auto_pv_common.param_string = JSON.stringify(params);
              }

              event = {
                info: {
                  auto_view_id: 'webview-abs|' + args.absolutePath,
                  data: {
                    auto_pv_common: auto_pv_common
                  },
                  operation: 'auto_view',
                  usage_id: 0
                },
                timestamp: timestamp,
                type: 'v3'
              };
              this.opts.bridgeCallHandler && this.opts.bridgeCallHandler(this.patch(event));

            case 6:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, this);
    }));

    function sendPageview(_x) {
      return _sendPageview.apply(this, arguments);
    }

    return sendPageview;
  }();

  _proto.patch = function patch(event) {
    if (!this.opts.matchRoutes) {
      return event;
    } // Patch auto_view_id with name in router config.


    var route = this.opts.matchRoutes(event.info.data.auto_pv_common.auto_pathname);

    if (route) {
      event.info.auto_view_id = "webview|" + route;
    } // Patch referrer with name in router config.


    var refer = event.info.data.auto_pv_common.auto_refer;

    if (refer && refer.startsWith('/')) {
      route = this.opts.matchRoutes(refer);

      if (route) {
        event.info.data.auto_pv_common.auto_refer = "webview|" + route;
      }
    }

    return {
      payload: event
    };
  }
  /**
   * Determines whether or not the tracker should send a hit with the new page
   * data. This default implementation can be overrided in the config options.
   * @param {string} newPath The path after the URL change.
   * @param {string} oldPath The path prior to the URL change.
   * @return {boolean} Whether or not the URL change should be tracked.
   */
  ;

  _proto.shouldTrackHistoryChange = function shouldTrackHistoryChange(newPath, oldPath) {
    // !! - converts to boolean
    return !!(newPath && oldPath);
  }
  /**
   * Sends auto_view event if a new history entry was created.
   * @param {boolean} historyDidUpdate True if the history was changed via
   * `pushState()` or the `popstate` event. False if the history was just
   * modified via `replaceState()`.
   */
  ;

  _proto.handleHistoryChange = function handleHistoryChange(historyDidUpdate) {
    var _this4 = this;

    var oldPath = this.path;
    var oldPathname = this.pathname;
    var newPath = getPathSearch();
    var newPathname = getPathname(location);

    if (oldPath !== newPath && this.opts.shouldTrackHistoryChange.call(this, newPath, oldPath)) {
      this.path = newPath;
      this.pathname = newPathname;

      if (historyDidUpdate || this.opts.trackReplaceState) {
        // Snapshot!
        var data = {
          isOnload: false,
          referrer: oldPathname,
          href: self.location.href,
          hostname: self.location.hostname,
          pathname: this.pathname,
          absolutePath: getAbsolutePath()
        }; // Call the update logic asychronously to help ensure that app logic
        // responding to the URL change happens prior to this.

        setTimeout(function () {
          try {
            _this4.sendPageview(data);
          } catch (err) {
            console.warn('[AutoTracker]', err);
          }
        }, 0);
      }
    }
  };

  return AutoTracker;
}();

var autoTracker = null; // Bridge call handler registered in native app.

var bridgeCallHandler = null; // Whether SDK has been initialized.

var initialized = false;
/**
 * A simplified version of TrackingV3SDK which should only be used in a web app that
 * is running inside WebView in a iOS/Android app. Basically it just acts as bridging
 * tracking points from web app to native app. Only v3 events are supported.
 */

var TrackingV3SDKWV =
/*#__PURE__*/
function () {
  function TrackingV3SDKWV() {}

  /**
   * Initialize AutoTracker.
   * @param {*} opts
   */
  TrackingV3SDKWV.init = function init(opts) {
    if (initialized) {
      console.warn('[TrackingV3SDKWV]', 'already initialized');
      return;
    }

    var defaultOpts = {
      bridgeCallHandler: null,
      enableAutoTrack: true,
      matchRoutes: null,
      trackReplaceState: false
    };

    var finalOpts = _extends({}, defaultOpts, opts);

    if (!autoTracker && finalOpts.enableAutoTrack) {
      autoTracker = new AutoTracker({
        bridgeCallHandler: finalOpts.bridgeCallHandler,
        matchRoutes: finalOpts.matchRoutes,
        trackReplaceState: finalOpts.trackReplaceState
      });
    }

    bridgeCallHandler = finalOpts.bridgeCallHandler;
    initialized = true;
  }
  /**
   * Send event data to native app by bridge web interface.
   * @param {*} data a v3 event message
   */
  ;

  TrackingV3SDKWV.sync = function sync(data) {
    if (!initialized) {
      console.warn('[TrackingV3SDKWV]', 'call TrackingV3SDKWV.init() at first');
      return;
    }

    bridgeCallHandler && bridgeCallHandler({
      payload: data
    });
  };

  return TrackingV3SDKWV;
}();

export default TrackingV3SDKWV;
